import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellRing, X } from 'lucide-react';
import { isValidUrl, isExternalUrl } from '@/lib/link-sanitizer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { cn } from "@/utils/cn";

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'achievement';
  timestamp: string;
  read: boolean;
  actionUrl?: string;
}

interface NotificationBellProps {
  notifications?: Notification[];
  onNotificationRead?: (notificationId: string) => void;
  onNotificationClear?: (notificationId: string) => void;
  onMarkAllRead?: () => void;
  className?: string;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  notifications = [],
  onNotificationRead,
  onNotificationClear,
  onMarkAllRead,
  className
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [localNotifications, setLocalNotifications] = useState<Notification[]>(notifications);
  const [pendingNavigationUrl, setPendingNavigationUrl] = useState<string | null>(null);

  useEffect(() => {
    setLocalNotifications(notifications);
  }, [notifications]);

  // Handle navigation in useEffect to avoid side effects during render
  useEffect(() => {
    if (pendingNavigationUrl) {
      // Security: Validate URL scheme before navigation
      if (!isValidUrl(pendingNavigationUrl)) {
        console.warn('NotificationBell: Blocked navigation to invalid URL:', pendingNavigationUrl);
        setPendingNavigationUrl(null);
        return;
      }

      // Check if it's an internal route or external URL
      if (isExternalUrl(pendingNavigationUrl)) {
        window.location.href = pendingNavigationUrl;
      } else {
        navigate(pendingNavigationUrl);
      }
      setPendingNavigationUrl(null);
    }
  }, [pendingNavigationUrl, navigate]);

  const unreadCount = localNotifications.filter(n => !n.read).length;
  const hasUnread = unreadCount > 0;

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      const updatedNotifications = localNotifications.map(n =>
        n.id === notification.id ? { ...n, read: true } : n
      );
      setLocalNotifications(updatedNotifications);
      onNotificationRead?.(notification.id);
    }

    if (notification.actionUrl) {
      setPendingNavigationUrl(notification.actionUrl);
    }
  };

  const handleClearNotification = (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updatedNotifications = localNotifications.filter(n => n.id !== notificationId);
    setLocalNotifications(updatedNotifications);
    onNotificationClear?.(notificationId);
  };

  const handleMarkAllRead = () => {
    const updatedNotifications = localNotifications.map(n => ({ ...n, read: true }));
    setLocalNotifications(updatedNotifications);
    onMarkAllRead?.();
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'achievement':
        return '🏆';
      default:
        return 'ℹ️';
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("relative", className)}
        >
          {hasUnread ? (
            <BellRing className="size-5" />
          ) : (
            <Bell className="size-5" />
          )}
          {hasUnread && (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 flex size-5 items-center justify-center p-0 text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
          <span className="sr-only">
            {hasUnread ? `${unreadCount} unread notifications` : 'No unread notifications'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" sideOffset={5}>
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Notifications</CardTitle>
              {hasUnread && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllRead}
                  className="text-xs"
                  aria-label="Mark all notifications as read"
                >
                  Mark all read
                </Button>
              )}
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="p-0">
            {localNotifications.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                <Bell className="mx-auto mb-2 size-8 opacity-50" />
                <p>No notifications</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {localNotifications.map((notification, index) => (
                  <div key={notification.id}>
                    <div
                      className={cn(
                        "cursor-pointer p-4 transition-colors hover:bg-muted/50",
                        !notification.read && "bg-primary/5"
                      )}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 shrink-0">
                          <span className="text-lg" role="img" aria-label={notification.type}>
                            {getNotificationIcon(notification.type)}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className={cn(
                                "text-sm font-medium leading-tight",
                                !notification.read && "font-semibold"
                              )}>
                                {notification.title}
                              </p>
                              <p className="mt-1 text-sm leading-tight text-muted-foreground">
                                {notification.message}
                              </p>
                              <div className="mt-2 flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">
                                  {formatTimestamp(notification.timestamp)}
                                </span>
                                {!notification.read && (
                                  <Badge variant="secondary" className="h-4 text-xs">
                                    New
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="size-6 p-0 opacity-60 hover:opacity-100"
                              onClick={(e) => handleClearNotification(notification.id, e)}
                              aria-label={`Clear notification: ${notification.title}`}
                            >
                              <X className="size-3" />
                              <span className="sr-only">Clear notification</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                    {index < localNotifications.length - 1 && <Separator />}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </PopoverContent>
    </Popover>
  );
};

